import React, { useEffect } from 'react';
import { updateSocialMeta } from '../utils/seo';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Trophy, Users, Calendar, Award, Target } from 'lucide-react';

const Leagues = () => {
  useEffect(() => {
    document.title = 'Golf Leagues - Pin High Golf & Lounge | Join a League in Bay Village';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute(
        'content',
        'Join competitive and social golf leagues at Pin High in Bay Village. Next season starts November 9th. Weekly formats, stat tracking, prizes, and great company. All skill levels welcome. Sign up today!'
      );
    }

    // Update canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', 'https://pinhighbay.com/leagues');
    updateSocialMeta({
      title: 'Golf Leagues - Pin High Golf & Lounge | Join a League in Bay Village',
      description:
        'Join competitive and social golf leagues at Pin High in Bay Village. Next season starts November 9th. Weekly formats, stat tracking, prizes, and great company. All skill levels welcome. Sign up today!',
      path: '/leagues',
    });
  }, []);

  const leagueTypes = [
    {
      title: 'Tee Up Your Mondays!',
      time: 'Mondays · 5:15 PM or 7:45 PM',
      format: '4-Person Team\nScramble\n18 Hole',
      duration: '8 Weeks',
      skill: 'All Levels',
      description:
        'Grab your foursome and join the ultimate indoor golf experience. Our 4-Person Golf Simulator League is the perfect way to kick off your week.',
      nextStart: 'Next Start: November 9th',
      price: '$75 per person ($300 per team)',
      ctaLabel: 'Register for Tee Up Your Mondays',
      ctaUrl: 'https://square.link/u/R0w6LleJ',
      icon: Users,
    },
    {
      title: 'Dynamic Duos, Assemble!',
      time: 'Tuesdays · 5:15 PM or 7:45 PM',
      format: '2-Person Team\nScramble\n18 Hole',
      duration: '8 Weeks',
      skill: 'All Levels',
      description:
        'Looking for a reason to get out and play? Our 2-Person Golf Simulator League is back! Team up with your favorite golf partner for 8 weeks of friendly competition.',
      nextStart: 'Next Start: November 10th',
      price: '$75 per person ($150 per team)',
      ctaLabel: 'Register for Dynamic Duos',
      ctaUrl: 'https://square.link/u/t5kkVCjz',
      icon: Target,
    },
    {
      title: 'Wednesday Night Duos',
      time: 'Wednesdays · 5:15 PM or 7:45 PM',
      format: '2-Person Team\nScramble\n9 Hole',
      duration: '8 Weeks',
      skill: 'All Levels',
      description:
        'New this season — a faster 9-hole, 2-person scramble on Wednesday nights. Friendly competition with a quicker round.',
      nextStart: 'Next Start: November 11th',
      price: '$75 per person ($150 per team)',
      ctaLabel: 'Register for Wednesday Night Duos',
      ctaUrl:
        'https://checkout.square.site/merchant/MLTWKYCV1T83N/checkout/SW5TICIPVQOQTLC2ZNIUAJFC',
      icon: Award,
    },
    {
      title: 'Thursdays Tee Off!',
      time: 'Thursdays · 5:15 PM or 7:45 PM',
      format: '4-Person Team\nScramble\n9 Hole',
      duration: '8 Weeks',
      skill: 'All Levels',
      description:
        'A fun and competitive Thursday golf league featuring 4-person teams playing 9 holes of scramble. Weekly matchups, good vibes, and a great way to kick off the weekend on the course.',
      nextStart: 'Next Start: November 12th',
      price: '$75 per person ($300 per team)',
      ctaLabel: 'Register for Thursdays Tee Off',
      ctaUrl: 'https://square.link/u/i3wZg8SD',
      icon: Trophy,
    },
    {
      title: 'Rise + Drive!',
      time: 'Saturdays or Sundays · 10:00 AM or 12:30 PM',
      format: '2-Person Team\nScramble\n18 Hole',
      duration: '8 Weeks',
      skill: 'All Levels',
      description:
        'Weekend golf league built for early risers and big hitters. Featuring 2-person teams playing an 18-hole scramble—fast-paced, competitive, and the perfect way to start the day on the course.',
      nextStart: 'Next Start: November 14th or 15th',
      price: '$75 per person ($150 per team)',
      ctaLabel: 'Register for Rise + Drive',
      ctaUrl: 'https://square.link/u/cNLrxefp',
      icon: Calendar,
    },
    {
      title: 'Open Tee Partners',
      time: 'Flexible · at available league times',
      format: '2-Person Team\nScramble\n18 Hole',
      duration: '8 Weeks',
      skill: 'All Levels',
      description:
        'A flexible 2-person golf league (Rotation) where teams can play at available league times during the week. Play on your schedule while competing in a relaxed but competitive format.',
      nextStart: 'Next Start: November 9th - 15th',
      price: '$75 per person ($150 per team)',
      ctaLabel: 'Register for Open Tee Partners',
      ctaUrl: 'https://square.link/u/yvmu1tAH',
      icon: Award,
    },
    {
      title: "Women's League",
      time: 'Play any day · at that day’s league times',
      format: '2-Person Team\nScramble\n9 Hole',
      duration: '8 Weeks',
      skill: "All Levels (Women's League)",
      description:
        'Wine, wedges, and a little friendly competition. Join our Women’s League — a relaxed 9-hole, 2-person scramble you can play any day that works for you.',
      nextStart: 'Next Start: November 9th',
      price: '$75 per person ($150 per team)',
      ctaLabel: "Register for Women's League",
      ctaUrl: 'https://square.link/u/zEDepMwJ',
      icon: Target,
    },
    {
      title: 'SIN Sunday Night',
      time: 'Sundays · 5:30 PM',
      format: '4-Person Team\nScramble\n18 Hole',
      duration: '8 Weeks',
      skill: 'All Levels (Service Industry Focused)',
      description:
        'This league is for all the SIN (Service Industry Night) teams! Four-person teams compete in a fun, social scramble with relaxed vibes, friendly competition, and a perfect way to unwind after a long weekend of work.',
      nextStart: 'Next Start: November 15th',
      price: '$150 per team',
      ctaLabel: 'Register for SIN Sunday Night',
      ctaUrl: 'https://square.link/u/UmfnmoGB',
      icon: Users,
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative h-[70vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="/leagues-hero.jpg"
            alt="Golf league players at Pin High Bay Village"
            className="w-full h-full object-cover opacity-90"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/40"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <Trophy className="w-20 h-20 mx-auto mb-6 text-green-400" />
          <h1 className="text-5xl md:text-7xl font-bold mb-6 tracking-tight">
            Join a League
          </h1>
          <p className="text-lg md:text-xl font-semibold text-green-400 mb-4">
            Next season starts November 9th
          </p>
          <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto text-gray-200">
            Designed for golfers of every level. Join a seasonal league with friends, meet new players, and play on world-class virtual courses — all from the comfort of our state-of-the-art simulators.
          </p>
        </div>
      </section>

      {/* League Options */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-gray-900">
              Current League Offerings
            </h2>
            <div className="text-xl text-gray-600 max-w-3xl mx-auto space-y-4">
              <p className="font-semibold text-gray-900">
                WHETHER YOU&apos;RE HERE TO SHARPEN YOUR SWING OR JUST ENJOY SOME FRIENDLY COMPETITION…
              </p>
              <p>
                Each league includes weekly play, rotating formats, live scoring, and prizes for top performers. From serious golfers to social sippers, there&apos;s a spot for everyone.
              </p>
              <p>
                Grab your clubs (or borrow ours), rally your crew, and tee up for a little friendly rivalry — all season long at Pin High.
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {leagueTypes.map((league, index) => {
              const IconComponent = league.icon;
              return (
                <Card
                  key={index}
                  className="border-none shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2"
                >
                  <CardContent className="p-8">
                    <div className="flex items-start gap-4 mb-6">
                      <div className="w-14 h-14 bg-green-600 rounded-lg flex items-center justify-center flex-shrink-0">
                        <IconComponent className="w-8 h-8 text-white" />
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-gray-900 mb-2">{league.title}</h3>
                        <p className="text-green-600 font-semibold">{league.time}</p>
                      </div>
                    </div>
                    <p className="text-gray-700 mb-6">{league.description}</p>
                    {league.nextStart && (
                      <div className="mb-4">
                        <p className="text-sm font-semibold text-gray-900">
                          {league.nextStart}
                        </p>
                        {league.price && (
                          <>
                            <p className="text-sm font-semibold text-green-600 mt-1">
                              {league.price}
                            </p>
                            <p className="text-sm text-gray-600 mt-1">
                              Price Includes simulator time each week
                            </p>
                          </>
                        )}
                      </div>
                    )}
                    {league.ctaUrl && league.ctaLabel && (
                      <div className="mt-2 mb-4">
                        <Button
                          asChild
                          size="sm"
                          className="bg-green-600 hover:bg-green-700 text-white"
                        >
                          <a
                            href={league.ctaUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {league.ctaLabel}
                          </a>
                        </Button>
                      </div>
                    )}
                    <div className="grid grid-cols-3 gap-4 py-4 border-t border-gray-200">
                      <div>
                        <p className="text-xs text-gray-600 mb-1">Format</p>
                        <p className="font-semibold text-gray-900 text-sm whitespace-pre-line">
                          {league.format}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-600 mb-1">Duration</p>
                        <p className="font-semibold text-gray-900 text-sm">{league.duration}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-600 mb-1">Skill Level</p>
                        <p className="font-semibold text-gray-900 text-sm">{league.skill}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-gray-900">
              What You Get in Every League
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="border-none shadow-lg text-center">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Calendar className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-xl font-bold mb-3 text-gray-900">8 Weeks Golf</h3>
                <p className="text-gray-600">8 Weeks of competitive golf</p>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg text-center">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Trophy className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-xl font-bold mb-3 text-gray-900">Prizes</h3>
                <p className="text-gray-600">Prizes for the winning team</p>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg text-center">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Target className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-xl font-bold mb-3 text-gray-900">Drink Specials</h3>
                <p className="text-gray-600">Happy Hour prices during league play.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-green-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Calendar className="w-16 h-16 mx-auto mb-6" />
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to Join the Competition?
          </h2>
          <p className="text-xl mb-8 text-green-50">
            Registration is now open for our upcoming season starting November 9th. Spots fill quickly, so secure your place today.
          </p>
        </div>
      </section>
    </div>
  );
};

export default Leagues;
